import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// AuthCallback runs once when the browser lands on `/#session_id=...` after
// completing the Emergent Google auth flow. We hand the session_id to our
// backend, which verifies it with Emergent, upserts the Reads user, and
// sets the JWT cookies — then we route the user into the app.
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  // useRef guard prevents StrictMode double-effect from trying to exchange the
  // one-time session_id twice.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    (async () => {
      try {
        await api.post("/auth/google/exchange", { session_id: sessionId });
        await refresh?.();
        // Clear the hash so a page-refresh won't try to re-exchange.
        window.history.replaceState({}, "", window.location.pathname);
        toast.success("Signed in with Google");
        navigate("/discover", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Google sign-in failed. Please try again.";
        toast.error(msg);
        navigate("/login", { replace: true });
      }
    })();
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="w-6 h-6 mx-auto text-[#525252] animate-spin" strokeWidth={1.5} />
        <div className="font-mono uppercase tracking-widest text-xs text-[#525252] mt-3">Finishing sign-in…</div>
      </div>
    </div>
  );
}
